import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { authApi } from '@/api/auth.api';
import { useAuthStore } from '@/stores/authStore';
import type { LoginPayload } from '@/types/auth';
import { ApiClientError } from '@/types/api';

export function useAuth() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user, setSession, clearSession, isAuthenticated, hasPermission, hasAnyPermission, hasRole } = useAuthStore();

  const loginMutation = useMutation({
    mutationFn: (payload: LoginPayload) => authApi.login(payload),
    onSuccess: (session) => {
      setSession(session);
      navigate('/', { replace: true });
    },
  });

  const logoutMutation = useMutation({
    mutationFn: () => authApi.logout(),
    onSettled: () => {
      clearSession();
      queryClient.clear();
      navigate('/login', { replace: true });
    },
  });

  return {
    user,
    isAuthenticated: isAuthenticated(),
    hasPermission,
    hasAnyPermission,
    hasRole,
    login: loginMutation.mutateAsync,
    isLoggingIn: loginMutation.isPending,
    loginError: loginMutation.error instanceof ApiClientError ? loginMutation.error.message : null,
    logout: logoutMutation.mutate,
    isLoggingOut: logoutMutation.isPending,
  };
}
